import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { Backdrop, Box, Paper, Typography, Zoom } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { burst } from '../../../../utils/confetti';

/** A single celebration to present in the centre of the screen. */
export interface CelebrationRequest {
	/** Optional bold heading (e.g. "Badge unlocked"). */
	title?: string;
	/** The main celebratory message. Required. */
	message: string;
	/** Large emoji shown above the text. Defaults to a party popper. */
	emoji?: string;
	/** How long the overlay stays before auto-dismissing. Defaults to 4500ms. */
	durationMs?: number;
}

interface CelebrationContextValue {
	/** Queue a centred celebration overlay with confetti. */
	celebrate: (request: CelebrationRequest) => void;
}

const CelebrationContext = createContext<CelebrationContextValue | undefined>(undefined);

const DEFAULT_DURATION_MS = 4500;

/**
 * Provides a single, app-wide "moment" — a centred, animated card with a
 * confetti burst — used for badge unlocks and admin easter eggs. Celebrations
 * are queued so overlapping triggers play one after another rather than
 * clobbering each other.
 */
export const CelebrationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { t } = useTranslation();
	const [current, setCurrent] = useState<CelebrationRequest | undefined>(undefined);
	const queueRef = useRef<CelebrationRequest[]>([]);
	const timerRef = useRef<number | undefined>(undefined);

	const showNext = useCallback(() => {
		const next = queueRef.current.shift();
		if (!next) {
			return;
		}
		setCurrent(next);
		burst({ x: 0.5, y: 0.35 });
		window.clearTimeout(timerRef.current);
		timerRef.current = window.setTimeout(() => {
			setCurrent(undefined);
		}, next.durationMs ?? DEFAULT_DURATION_MS);
	}, []);

	const celebrate = useCallback(
		(request: CelebrationRequest) => {
			queueRef.current.push(request);
			if (!current) {
				showNext();
			}
		},
		[current, showNext]
	);

	// When the current overlay clears, play the next queued celebration (if any).
	useEffect(() => {
		if (!current && queueRef.current.length > 0) {
			const id = window.setTimeout(showNext, 350);
			return () => window.clearTimeout(id);
		}
		return undefined;
	}, [current, showNext]);

	useEffect(() => () => window.clearTimeout(timerRef.current), []);

	const dismiss = useCallback(() => {
		window.clearTimeout(timerRef.current);
		setCurrent(undefined);
	}, []);

	const value = useMemo<CelebrationContextValue>(() => ({ celebrate }), [celebrate]);

	return (
		<CelebrationContext.Provider value={value}>
			{children}
			<Backdrop
				open={Boolean(current)}
				onClick={dismiss}
				sx={{
					zIndex: 2000,
					color: '#fff',
					backdropFilter: 'blur(3px)',
					backgroundColor: 'rgba(15, 17, 26, 0.55)'
				}}
			>
				<Zoom in={Boolean(current)}>
					<Paper
						elevation={24}
						role="status"
						aria-live="assertive"
						onClick={(e) => e.stopPropagation()}
						sx={{
							position: 'relative',
							maxWidth: 420,
							mx: 3,
							px: { xs: 4, sm: 6 },
							py: { xs: 4, sm: 5 },
							borderRadius: 4,
							textAlign: 'center',
							overflow: 'hidden',
							color: 'common.white',
							background:
								'linear-gradient(135deg, #7e57c2 0%, #42a5f5 45%, #26a69a 100%)',
							boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
							'&::before': {
								content: '""',
								position: 'absolute',
								inset: 0,
								background:
									'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.35), transparent 60%)',
								pointerEvents: 'none'
							},
							animation: 'celebrationPop 600ms ease',
							'@keyframes celebrationPop': {
								'0%': { transform: 'scale(0.85)' },
								'55%': { transform: 'scale(1.04)' },
								'100%': { transform: 'scale(1)' }
							}
						}}
					>
						<Box
							sx={{
								fontSize: { xs: 56, sm: 72 },
								lineHeight: 1,
								mb: 1.5,
								animation: 'celebrationFloat 2.4s ease-in-out infinite',
								'@keyframes celebrationFloat': {
									'0%, 100%': { transform: 'translateY(0)' },
									'50%': { transform: 'translateY(-8px)' }
								}
							}}
							aria-hidden
						>
							{current?.emoji ?? '🎉'}
						</Box>
						{current?.title && (
							<Typography
								variant="overline"
								sx={{ letterSpacing: 2, opacity: 0.9, display: 'block', mb: 0.5 }}
							>
								{current.title}
							</Typography>
						)}
						<Typography variant="h5" sx={{ fontWeight: 700, textShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
							{current?.message}
						</Typography>
						<Typography variant="caption" sx={{ display: 'block', mt: 2.5, opacity: 0.75 }}>
							{t('pages.homepage.celebration.dismissHint')}
						</Typography>
					</Paper>
				</Zoom>
			</Backdrop>
		</CelebrationContext.Provider>
	);
};

/**
 * Access the app-wide celebration trigger. Must be called within a
 * {@link CelebrationProvider}.
 */
export const useCelebration = (): CelebrationContextValue => {
	const ctx = useContext(CelebrationContext);
	if (!ctx) {
		throw new Error('useCelebration must be used within a CelebrationProvider');
	}
	return ctx;
};
